import { Routes } from '@angular/router';
import { BasePageComponent } from './core/components/base-page/base-page.component';
import { LoginFormComponent } from './pages/login/components/login-form/login-form.component';
import { AuthService } from './services/auth.service';
import { inject } from '@angular/core';
import { CommunityLandingComponent } from './pages/community/components/community-landing/community-landing.component';
import { CommunityComponent } from './pages/community/components/community/community.component';
import { CommunityLayoutComponent } from './pages/community/community-layout.component';
import { communityResolver } from './pages/community/resolvers/community.resolver';
import { AccountRequestComponent } from './pages/login/components/account-request/account-request.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { ReportsComponent } from './pages/reports/reports.component';
import { LandingResolver } from './pages/landing/landing.resolver';
import { reportResolver } from './pages/reports/resolvers/report.resolver';
import {
  RestorationCurveReportComponent,
} from './pages/reports/components/restoration-curve-report/restoration-curve-report.component';
import { CoverageReportComponent } from './pages/reports/components/coverage-report/coverage-report.component';
import { LocationService } from './services/location.service';
import {
  WeatherBaselineReportComponent,
} from './pages/reports/components/weather-baseline-report/weather-baseline-report.component';
import { ForgotPasswordComponent } from './pages/login/components/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/login/components/reset-password/reset-password.component';
import { VerifyEmailComponent } from './pages/login/components/verify-email/verify-email.component';
import { UserManagementComponent } from './pages/user-management/user-management.component';
import { userRoleGuard } from './pages/user-management/user-role.guard';
import { userResolver } from './pages/profile/resolvers/user.resolver';
import { GenRoleDefinition } from './generated/serverModels/GenRoleDefinition';
import { NewLandingComponent } from './pages/landing/new-landing/new-landing.component';
import { MapLandingComponent } from './pages/landing/map-landing.component';
import { LayerManagementComponent } from './pages/admin-controls/components/layer-management/layer-management.component';
import { UserAccessManagementComponent } from './pages/admin-controls/components/user-access-management/user-access-management.component';
import { ReleaseNotesComponent } from './core/components/release-notes/release-notes.component';
import { HelpLandingComponent } from './pages/help/components/help-landing/help-landing.component';
import { StateReportHelperComponent } from './pages/reports/components/state-report-helper/state-report-helper.component';
import {
  ApiKeyManagementComponent
} from "./pages/admin-controls/components/api-key-management/api-key-management.component";

export const routes: Routes = [
  {
    path: 'login',
    children: [
      {
        path: '',
        component: LoginFormComponent,
      },
      {
        path: 'account-request',
        component: AccountRequestComponent,
      },
      {
        path: 'forgot-password',
        component: ForgotPasswordComponent,
      },
      {
        path: 'change-password/:token',
        component: ResetPasswordComponent,
      },
      {
        path: 'verify-email/:code',
        component: VerifyEmailComponent,
      },
    ],
  },
  {
    path: 'app',
    canActivate: [() => inject(AuthService).isLoggedIn()],
    component: BasePageComponent,
    resolve: {
      territories: () => inject(LocationService).getAllTerritories(),
    },
    children: [
      {
        path: '',
        component: NewLandingComponent,
        resolve: { landing: LandingResolver },
      },
      {
        path: 'map',
        component: MapLandingComponent,
      },
      {
        path: 'help',
        children: [
          {
            path: '',
            component: HelpLandingComponent,
          },
          {
            path: 'release-notes',
            component: ReleaseNotesComponent,

          }],
      },
      {
        path: 'communities',
        component: CommunityLayoutComponent,
        children: [
          {
            path: '',
            component: CommunityLandingComponent,
          },
          {
            path: ':id',
            component: CommunityComponent,
            resolve: { community: communityResolver },
          },
        ],
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            component: ProfileComponent,
            resolve: {
              user: userResolver,
              territories: () => inject(LocationService).getAllTerritories(),
            },
          },
          {
            path: ':id',
            resolve: {
              user: userResolver,
            },
            component: ProfileComponent,
          },
        ],
      },
      {
        path: 'reports',
        children: [
          {
            path: '',
            component: ReportsComponent,
          },
          {
            path: 'restoration-curve',
            component: RestorationCurveReportComponent,
            resolve: { report: reportResolver },
          },
          {
            path: 'county-coverage',
            component: CoverageReportComponent,
            resolve: { report: reportResolver },
          },
          {
            path: 'weather-baseline',
            component: WeatherBaselineReportComponent,
            resolve: { report: reportResolver },
          },
          {
            path: 'state-helper',
            component: StateReportHelperComponent,
          },
          {
            path: 'state-helper/:stateAbbr/:year/:page',
            component: StateReportHelperComponent,
          },
        ],
      },
      {
        path: 'admin-controls',
        children: [
          {
            path: 'layer-management',
            component: LayerManagementComponent,
            canActivate: [userRoleGuard],
            data: {
              requiredRoles: [GenRoleDefinition.ROLE_ADMIN],
            },
          },
          {
            path: 'user-management',
            component: UserManagementComponent,
            canActivate: [userRoleGuard],
            data: {
              requiredRoles: [GenRoleDefinition.ROLE_USER_MANAGER],
            },
          },
          {
            path: 'apikey-management',
            component: ApiKeyManagementComponent,
            canActivate: [userRoleGuard],
            data: {
              requiredRoles: [GenRoleDefinition.ROLE_USER_MANAGER]
            },
          },
          {
            path: 'user-access-management',
            component: UserAccessManagementComponent,
            canActivate: [userRoleGuard],
            data: {
              requiredRoles: [GenRoleDefinition.ROLE_ADMIN, GenRoleDefinition.ROLE_USER_MANAGER],
            },
          },
        ],
      },
    ],
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: 'app',
  },
];
